import { Info, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

/**
 * ApplicationLaunchDialog — surfaces informational payloads emitted by the
 * Application Launch Service (currently used for `desktop_launcher` and
 * `none` launch methods). Kept dumb: no data fetching, purely presentational.
 *
 * `payload` shape (from applicationLauncher.js):
 *   { kind, title, body, app }
 */
export default function ApplicationLaunchDialog({ payload, onClose }) {
  if (!payload) return null;
  const { app } = payload;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="application-launch-dialog"
        className="max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Info size={20} weight="fill" className="text-brand" />
            {payload.title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Informational dialog with details about the selected application.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            data-testid="application-launch-dialog-app-details"
            className="rounded-md border border-slate-200 bg-slate-50 p-3"
          >
            <div className="text-sm font-semibold text-slate-900">{app.name}</div>
            <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
              <DetailRow label="Type" value={app.app_type} testid="dialog-app-type" />
              <DetailRow label="Status" value={app.status} testid="dialog-app-status" />
              {app.version && (
                <DetailRow label="Version" value={`v${app.version}`} mono testid="dialog-app-version" />
              )}
              {app.owner_name && (
                <DetailRow label="Owner" value={app.owner_name} testid="dialog-app-owner" />
              )}
            </dl>
          </div>

          <p className="text-sm leading-relaxed text-slate-700">{payload.body}</p>
        </div>

        <DialogFooter>
          <Button
            data-testid="application-launch-dialog-close"
            onClick={onClose}
            className="bg-charcoal hover:bg-charcoal"
          >
            <X size={14} weight="bold" className="mr-1" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ label, value, mono, testid }) {
  return (
    <div className="flex justify-between gap-2 sm:block">
      <dt className="text-[11px] uppercase tracking-wider text-slate-500">{label}</dt>
      <dd
        data-testid={testid}
        className={`text-sm font-medium text-slate-800 ${mono ? "font-mono" : ""}`}
      >
        {value}
      </dd>
    </div>
  );
}
